<template>
  <div
    v-if="isOpen"
    id="more-info-modal"
    class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[999999] p-4"
  >
    <div class="modal-container bg-white w-full max-w-5xl rounded-2xl shadow-2xl overflow-hidden relative animate-modal-in">
      <!-- Modal Header -->
      <div class="modal-header">
        <div class="header-content">
          <div class="header-icon">
            <i class="fas fa-info-circle"></i>
          </div>
          <div class="header-text">
            <h2 class="modal-title">{{ placeName }}</h2>
            <span class="modal-subtitle">More Information</span>
          </div>
        </div>
        <div class="header-actions">
          <!-- Quiz Button -->
          <button
            v-if="hasArticleData"
            @click="goToQuiz"
            class="quiz-button"
            title="Take Quiz"
            aria-label="Take Quiz"
          >
            <i class="fas fa-graduation-cap"></i>
          </button>

          <!-- Star Button -->
          <StarButton
            v-if="hasArticleData && articleId"
            :item-id="articleId"
            item-type="article"
            :item-data="{ title, slug }"
            size="medium"
            variant="compact"
          />

          <!-- Share Button -->
          <button
            v-if="hasArticleData"
            @click="shareArticle"
            class="share-button"
            title="Share this article"
            aria-label="Share Article"
          >
            <i class="fas fa-share-alt"></i>
          </button>

          <button
            @click="closeModal"
            class="close-button"
            aria-label="Close Modal"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Content Container -->
      <div class="content-container">
        <!-- Loading State -->
        <div v-if="loading" class="loading-container">
          <div class="modern-loader"></div>
          <p class="loading-text">Fetching information...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-container">
          <div class="error-icon">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
          <h3 class="error-title">No Information Found</h3>
          <p class="error-message">{{ error }}</p>
          <button @click="closeModal" class="error-button">
            <i class="fas fa-arrow-left"></i>
            Go Back
          </button>
        </div>

        <!-- Article Display -->
        <div v-else-if="hasArticleData" class="article-container">
          <!-- Article Header -->
          <div class="article-header">
            <h1 class="article-title">{{ title }}</h1>
            <p class="article-introduction">{{ introduction }}</p>
          </div>
          
          <!-- Article Content with Integrated Images -->
          <div class="article-content" v-html="processedContent"></div>
          
          <!-- Article Rating -->
          <ArticleRating
            v-if="hasArticleData"
            :article-id="null"
            :article-slug="slug"
            :is-authenticated="isAuthenticated"
            :auth-token="authToken"
            @show-auth="handleShowAuth"
            @vote-submitted="handleVoteSubmitted"
          />
          
          <!-- Article Footer -->
          <div class="article-footer">
            <div class="footer-content">
              <div class="footer-branding">
                <i class="fas fa-globe-americas"></i>
                <span>Captain Leo's Maps Explorer</span>
              </div>
              <button @click="viewFullArticle" class="full-article-btn">
                <i class="fas fa-external-link-alt"></i>
                View Full Article
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-container">
          <div class="empty-icon">
            <i class="fas fa-search"></i>
          </div>
          <h3 class="empty-title">No Content Available</h3>
          <p class="empty-message">We couldn't find additional information for this location.</p>
        </div>
      </div>

      <!-- Share Card (Desktop) -->
      <div v-if="showShareCard" class="share-overlay" @click.self="closeShareCard">
        <div class="share-card">
          <h4 class="share-title">Share Article</h4>
          <div class="share-url-container">
            <input ref="shareUrlInput" :value="shareUrl" readonly class="share-url-input">
            <button @click="copyUrl" class="copy-btn" :class="{ 'copied': urlCopied }">
              <i v-if="!urlCopied" class="fas fa-copy"></i>
              <i v-else class="fas fa-check"></i>
              {{ urlCopied ? 'Copied!' : 'Copy' }}
            </button>
          </div>
          <button @click="closeShareCard" class="close-share-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import ArticleRating from './ArticleRating.vue'
import StarButton from './StarButton.vue'

export default {
  name: 'MoreInfoModal',
  components: {
    ArticleRating,
    StarButton
  },
  data() {
    return {
      showShareCard: false,
      urlCopied: false,
      isMobile: window.innerWidth <= 768
    };
  },
  computed: {
    ...mapState('moreInfo', ['isOpen', 'placeName', 'title', 'introduction', 'body', 'images', 'html', 'loading', 'error', 'slug', 'placeSlug', 'itemSlug', 'articleId']),
    ...mapState('auth', ['token']),
    isAuthenticated() {
      return this.$store.getters['auth/isAuthenticated'];
    },
    authToken() {
      return this.token;
    },
    hasArticleData() {
      return this.title && this.body
    },
    processedContent() {
      if (!this.body || !this.images || this.images.length === 0) {
        return this.body
      }
      
      return this.injectImagesIntoContent(this.body, this.images)
    },
    shareUrl() {
      if (this.slug) {
        return `${window.location.origin}/article/${this.slug}`;
      }
      return '';
    }
  },
  methods: {
    ...mapActions('moreInfo', ['closeModal']),
    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.closeModal();
      }
    },
    openImageModal(image) {
      // Open the image in a new tab or use existing image modal
      window.open(image.link, '_blank');
    },
    handleImageError(event) {
      // Hide broken images
      event.target.style.display = 'none';
    },
    viewFullArticle() {
      // Open the full HTML article in a new tab
      if (this.html) {
        const newWindow = window.open('', '_blank');
        newWindow.document.write(this.html);
        newWindow.document.close();
      }
    },
    injectImagesIntoContent(htmlContent, images) {
      if (!images || images.length === 0) return htmlContent
      
      // Create a temporary div to parse the HTML
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = htmlContent
      
      // Split content into logical sections based on headings
      const contentSections = this.groupContentIntoSections(tempDiv)
      
      if (contentSections.length === 0) return htmlContent
      
      // Distribute images evenly across sections
      const sectionsPerImage = Math.max(1, Math.floor(contentSections.length / images.length))
      let imageIndex = 0
      let resultHtml = ''
      
      contentSections.forEach((section, sectionIndex) => {
        const shouldAddImage = imageIndex < images.length && 
                              (sectionIndex + 1) % sectionsPerImage === 0 && 
                              sectionIndex > 0
        
        if (shouldAddImage) {
          const image = images[imageIndex]
          const isEven = imageIndex % 2 === 0
          
          // Create content section with image
          resultHtml += `
            <div class="content-section ${isEven ? 'image-right' : 'image-left'}">
              <div class="text-content">
                ${section.html}
              </div>
              <div class="image-content">
                <div class="image-container">
                  <img src="${image.link}" 
                       alt="${image.title || 'Image'}" 
                       class="content-image"
                       onclick="window.open('${image.link}', '_blank')"
                       onerror="this.style.display='none'">
                  <div class="image-caption">
                    ${image.title || 'View larger image'}
                  </div>
                </div>
              </div>
            </div>
          `
          imageIndex++
        } else {
          // Just add the section content without image
          resultHtml += section.html
        }
      })
      
      return resultHtml
    },
    
    groupContentIntoSections(container) {
      const sections = []
      const children = Array.from(container.children)
      let currentSection = { elements: [], html: '' }
      
      children.forEach(element => {
        // Start a new section when we hit a major heading
        if (element.tagName === 'H2') {
          // Save previous section if it has content
          if (currentSection.elements.length > 0) {
            currentSection.html = currentSection.elements.map(el => el.outerHTML).join('')
            sections.push({ ...currentSection })
          }
          
          // Start new section with this heading
          currentSection = { elements: [element], html: '' }
        } else {
          // Add element to current section
          currentSection.elements.push(element)
        }
      })
      
      // Don't forget the last section
      if (currentSection.elements.length > 0) {
        currentSection.html = currentSection.elements.map(el => el.outerHTML).join('')
        sections.push(currentSection)
      }
      
      return sections
    },
    shareArticle() {
      if (this.isMobile) {
        this.shareOnMobile();
      } else {
        this.showShareCard = true;
      }
    },
    async shareOnMobile() {
      const shareData = {
        title: `${this.title} - Captain Leo Maps`,
        text: `Check out this interesting article: ${this.title}`,
        url: this.shareUrl
      };

      try {
        if (navigator.share) {
          await navigator.share(shareData);
        } else {
          // Fallback for browsers that don't support Web Share API
          this.copyUrl();
        }
      } catch (error) {
        console.log('Error sharing:', error);
        // Fallback to copy URL
        this.copyUrl();
      }
    },
    closeShareCard() {
      this.showShareCard = false;
      this.urlCopied = false;
    },
    async copyUrl() {
      try {
        await navigator.clipboard.writeText(this.shareUrl);
        this.urlCopied = true;
        setTimeout(() => {
          this.urlCopied = false;
          if (!this.isMobile) {
            this.showShareCard = false;
          }
        }, 2000);
      } catch (error) {
        console.log('Failed to copy URL:', error);
        // Fallback for older browsers
        if (this.$refs.shareUrlInput) {
          this.$refs.shareUrlInput.select();
          document.execCommand('copy');
          this.urlCopied = true;
          setTimeout(() => {
            this.urlCopied = false;
            if (!this.isMobile) {
              this.showShareCard = false;
            }
          }, 2000);
        }
      }
    },
    checkScreenSize() {
      this.isMobile = window.innerWidth <= 768;
    },
    handleShowAuth() {
      // Emit event to parent to show authentication modal/form
      // This could be handled by the main app or header component
      console.log('Show authentication requested');
      // You might want to emit an event or trigger a global auth modal here
      this.$emit('show-auth');
    },
    handleVoteSubmitted(voteData) {
      // Handle vote submission success
      console.log('Vote submitted:', voteData);
      // Could show a toast notification or update local state
    },
    goToQuiz() {
      // Navigate to quiz page for this article
      // The quiz will be identified by the article slug
      if (this.slug) {
        // Store slug in a local variable before closing modal
        const articleSlug = this.slug;
        const quizPath = `/quiz/article-${articleSlug}`;

        // Close modal before navigation
        this.closeModal();

        // Navigate to quiz using path instead of name/params
        // This ensures the slug is preserved in the URL
        this.$router.push(quizPath);
      } else {
        console.error('[MoreInfoModal] Cannot navigate to quiz - slug is empty!');
        alert('Unable to start quiz - article information is missing. Please try reopening the article.');
      }
    }
  },
  mounted() {
    window.addEventListener('resize', this.checkScreenSize);
    console.log('[MoreInfoModal] Mounted');
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScreenSize);
    document.removeEventListener('keydown', this.handleKeydown);
    document.body.style.overflow = 'auto';
  },
  watch: {
    isOpen(newValue) {
      if (newValue) {
        document.addEventListener('keydown', this.handleKeydown);
        document.body.style.overflow = 'hidden';
      } else {
        document.removeEventListener('keydown', this.handleKeydown);
        document.body.style.overflow = 'auto';
      }
    },
    articleId(newVal) {
      console.log('[MoreInfoModal] articleId changed:', newVal);
    },
    hasArticleData(newVal) {
      console.log('[MoreInfoModal] hasArticleData changed:', newVal);
      console.log('[MoreInfoModal] Current articleId:', this.articleId);
    }
  }
}
</script>

<style scoped>
@import '~@fortawesome/fontawesome-free/css/all.css';

/* Modal Animation */
@keyframes modalIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.animate-modal-in {
  animation: modalIn 0.3s ease-out;
}

/* Modal Container */
.modal-container {
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

/* Modal Header */
.modal-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.header-icon i {
  font-size: 20px;
  color: rgba(255, 255, 255, 0.9);
}

.header-text {
  display: flex;
  flex-direction: column;
}

.modal-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.modal-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  margin-top: 2px;
}

.close-button {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.close-button:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.close-button i {
  font-size: 16px;
}

/* Content Container */
.content-container {
  flex: 1;
  overflow-y: auto;
  min-height: 400px;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  gap: 20px;
}

.modern-loader {
  width: 64px;
  height: 64px;
  border: 6px solid rgba(102, 126, 234, 0.1);
  border-top: 6px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  color: #64748b;
  font-size: 16px;
  font-weight: 500;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error State */
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  text-align: center;
}

.error-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.error-icon i {
  font-size: 32px;
  color: white;
}

.error-title {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 12px 0;
}

.error-message {
  font-size: 16px;
  color: #64748b;
  line-height: 1.6;
  margin: 0 0 32px 0;
  max-width: 400px;
}

.error-button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 12px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.error-button:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

/* Empty State */
.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.empty-icon i {
  font-size: 32px;
  color: white;
}

.empty-title {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 12px 0;
}

.empty-message {
  font-size: 16px;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
  max-width: 400px;
}

/* Article Container */
.article-container {
  padding: 0;
}

/* Article Header */
.article-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 32px 24px;
  text-align: center;
}

.article-title {
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 16px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.article-introduction {
  font-size: 18px;
  line-height: 1.6;
  opacity: 0.95;
  margin: 0;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

/* Article Content */
.article-content {
  padding: 32px 24px;
  line-height: 1.8;
  color: #374151;
  font-size: 16px;
}

.article-content :deep(h2) {
  color: #1e293b;
  font-size: 22px;
  font-weight: 700;
  margin: 32px 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #e2e8f0;
}

.article-content :deep(h3) {
  color: #374151;
  font-size: 18px;
  font-weight: 600;
  margin: 24px 0 12px 0;
}

.article-content :deep(p) {
  margin: 0 0 16px 0;
  line-height: 1.7;
}

.article-content :deep(ul),
.article-content :deep(ol) {
  margin: 16px 0;
  padding-left: 24px;
}

.article-content :deep(li) {
  margin-bottom: 8px;
  line-height: 1.6;
}

.article-content :deep(strong) {
  font-weight: 600;
  color: #1e293b;
}

.article-content :deep(em) {
  font-style: italic;
  color: #4b5563;
}

/* Content Sections with Integrated Images */
.article-content :deep(.content-section) {
  display: flex;
  align-items: flex-start;
  gap: 32px;
  margin: 32px 0;
  padding: 24px 0;
  border-top: 1px solid #f1f5f9;
}

.article-content :deep(.content-section:first-child) {
  border-top: none;
  margin-top: 0;
}

.article-content :deep(.text-content) {
  flex: 1;
  min-width: 0;
}

.article-content :deep(.image-content) {
  flex: 0 0 300px;
}

/* Image Right Layout */
.article-content :deep(.image-right) {
  flex-direction: row;
}

/* Image Left Layout */
.article-content :deep(.image-left) {
  flex-direction: row-reverse;
}

.article-content :deep(.image-container) {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
  background: white;
}

.article-content :deep(.image-container):hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.2);
}

.article-content :deep(.content-image) {
  width: 100%;
  height: 200px;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.article-content :deep(.content-image):hover {
  transform: scale(1.02);
}

.article-content :deep(.image-caption) {
  padding: 12px 16px;
  background: white;
  color: #64748b;
  font-size: 13px;
  line-height: 1.4;
  border-top: 1px solid #f1f5f9;
  font-weight: 500;
}

/* Remove margins from text elements inside content sections */
.article-content :deep(.content-section .text-content h2) {
  margin-top: 0;
}

.article-content :deep(.content-section .text-content h3) {
  margin-top: 0;
}

.article-content :deep(.content-section .text-content p) {
  margin-bottom: 0;
}

/* Article Footer */
.article-footer {
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  padding: 24px;
}

.footer-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 600px;
  margin: 0 auto;
}

.footer-branding {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-weight: 500;
}

.footer-branding i {
  color: #667eea;
  font-size: 18px;
}

.full-article-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}

.full-article-btn:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

/* Legacy Content Display (for backward compatibility) */
.info-content {
  padding: 24px;
  line-height: 1.7;
  color: #374151;
}

.info-content :deep(h1),
.info-content :deep(h2),
.info-content :deep(h3) {
  color: #1e293b;
  margin-top: 24px;
  margin-bottom: 12px;
  font-weight: 700;
}

.info-content :deep(h1) {
  font-size: 28px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.info-content :deep(h2) {
  font-size: 22px;
}

.info-content :deep(h3) {
  font-size: 18px;
}

.info-content :deep(p) {
  margin-bottom: 16px;
}

.info-content :deep(ul),
.info-content :deep(ol) {
  margin: 16px 0;
  padding-left: 24px;
}

.info-content :deep(li) {
  margin-bottom: 8px;
}

.info-content :deep(a) {
  color: #667eea;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.info-content :deep(a):hover {
  color: #5a67d8;
  text-decoration: underline;
}

.info-content :deep(blockquote) {
  border-left: 4px solid #667eea;
  padding-left: 16px;
  margin: 20px 0;
  font-style: italic;
  color: #4b5563;
}

.info-content :deep(code) {
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 14px;
}

.info-content :deep(pre) {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  overflow-x: auto;
  margin: 16px 0;
}

/* Tablet Responsive */
@media (max-width: 1024px) and (min-width: 769px) {
  .article-content :deep(.image-content) {
    flex: 0 0 250px;
  }
  
  .article-content :deep(.content-section) {
    gap: 24px;
  }
  
  .article-content :deep(.content-image) {
    height: 160px;
  }
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .modal-container {
    max-width: 95vw;
    max-height: calc(95vh - 64px); /* Reduce height to account for top margin */
    margin: 80px 0 0 0; /* Add top margin to avoid search bar overlap */
    border-radius: 16px;
  }
  
  .modal-header {
    padding: 16px 20px;
  }
  
  .header-icon {
    width: 40px;
    height: 40px;
  }
  
  .header-icon i {
    font-size: 16px;
  }
  
  .modal-title {
    font-size: 20px;
  }
  
  .modal-subtitle {
    font-size: 12px;
  }
  
  .close-button {
    width: 40px;
    height: 40px;
  }
  
  .loading-container,
  .error-container,
  .empty-container {
    padding: 40px 20px;
  }
  
  .error-icon,
  .empty-icon {
    width: 64px;
    height: 64px;
    margin-bottom: 20px;
  }
  
  .error-icon i,
  .empty-icon i {
    font-size: 24px;
  }
  
  .error-title,
  .empty-title {
    font-size: 20px;
  }
  
  .error-message,
  .empty-message {
    font-size: 14px;
  }
  
  .info-content {
    padding: 20px;
  }
  
  .info-content :deep(h1) {
    font-size: 24px;
  }
  
  .info-content :deep(h2) {
    font-size: 20px;
  }
  
  .info-content :deep(h3) {
    font-size: 16px;
  }
  
  /* Mobile Article Styles */
  .article-header {
    padding: 24px 20px;
  }
  
  .article-title {
    font-size: 24px;
  }
  
  .article-introduction {
    font-size: 16px;
  }
  
  .article-content {
    padding: 24px 20px;
    font-size: 15px;
  }
  
  /* Mobile: Stack content sections vertically */
  .article-content :deep(.content-section) {
    flex-direction: column !important;
    gap: 20px;
    margin: 20px 0;
    padding: 20px 0;
  }
  
  .article-content :deep(.image-content) {
    flex: none;
    width: 100%;
  }
  
  .article-content :deep(.content-image) {
    height: 180px;
  }
  
  .article-content :deep(.image-caption) {
    padding: 10px 14px;
    font-size: 12px;
  }
  
  .footer-content {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
  
  .full-article-btn {
    font-size: 13px;
    padding: 8px 16px;
  }
}

/* Extra small screens */
@media (max-width: 480px) {
  .header-content {
    gap: 12px;
  }
  
  .modal-title {
    font-size: 18px;
  }
  
  .info-content {
    padding: 16px;
  }
}

/* Header action buttons */
.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.quiz-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.quiz-button:hover {
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.share-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.share-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.share-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 2rem;
}

.share-card {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  min-width: 400px;
  max-width: 90vw;
  position: relative;
  animation: shareCardIn 0.2s ease-out;
}

@keyframes shareCardIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.share-title {
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.share-url-container {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.share-url-input {
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  background: #f8f9fa;
  color: #333;
  font-family: 'Monaco', 'Consolas', monospace;
  word-break: break-all;
}

.copy-btn {
  padding: 12px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 100px;
  justify-content: center;
}

.copy-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.copy-btn.copied {
  background: linear-gradient(135deg, #10dc60 0%, #16a085 100%);
}

.close-share-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.close-share-btn:hover {
  background: #f0f0f0;
  color: #333;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .share-card {
    min-width: 320px;
    margin: 20px;
  }
  
  .share-url-container {
    flex-direction: column;
  }
  
  .copy-btn {
    width: 100%;
  }
}
</style>